import React from "react";
import { Link } from "react-router-dom";
import { Github, Linkedin, Mail } from "lucide-react";
import GitPrompt from "@/components/GitPrompt";
import { siteConfig } from "@/data/site";
import { useTimeTheme } from "@/hooks/use-time-theme";
import { openSourceProjects } from "@/data/opensource";

/** Format "YYYY-MM" to "Jan 2026"; pass-through other strings. */
function formatPRDate(raw: string): string {
  const match = raw.match(/^(\d{4})-(\d{2})$/);
  if (!match) return raw;
  const [, year, month] = match;
  const date = new Date(parseInt(year, 10), parseInt(month, 10) - 1);
  return date.toLocaleDateString("en-US", { month: "short", year: "numeric" });
}

const OpenSource = () => {
  const theme = useTimeTheme();

  return (
    <div className={`min-h-screen ${theme.background} ${theme.text} transition-colors duration-1000`}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header - Matching home page style */}
        <header className="pt-8 sm:pt-12 md:pt-16 pb-12 sm:pb-16 md:pb-20">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <Link
              to="/"
              className={`text-xl sm:text-2xl md:text-3xl font-bold ${theme.accent} hover:opacity-80 transition-all text-center sm:text-left`}
            >
              {siteConfig.name.split(" ")[0].toLowerCase()}.space
            </Link>

            {/* Navigation - Centered on mobile, right-aligned on large screens */}
            <nav className={`flex flex-wrap items-center justify-center sm:justify-end gap-4 sm:gap-6 text-sm sm:text-base ${theme.accent}/80`}>
              <Link to="/about" className="hover:opacity-100 transition-colors">
                About
              </Link>
              <Link to="/blog" className="hover:opacity-100 transition-colors">
                Blog
              </Link>
              <Link to="/projects" className="hover:opacity-100 transition-colors">
                Projects
              </Link>
              <Link to="/uses" className="hover:opacity-100 transition-colors">
                Uses
              </Link>
              <Link to="/opensource" className="hover:opacity-100 transition-colors">
                Open Source
              </Link>
            </nav>
          </div>
        </header>

        {/* Main Content */}
        <main className="pb-12 sm:pb-16">
          <section className="flex flex-col items-center">
            <div className="max-w-2xl w-full space-y-8">
              {/* Git Prompt */}
              <GitPrompt command="gh pr list --author @me" className="justify-center sm:justify-start" />

              {/* Header */}
              <div className="space-y-4">
                <h1 className={`text-2xl sm:text-3xl md:text-4xl font-bold leading-tight ${theme.text}`}>
                  Open Source
                </h1>
                <p className={`text-base sm:text-lg ${theme.text}/70`}>
                  Pull requests I&apos;ve opened across different open source projects.
                </p>
              </div>

              {/* Projects & PRs */}
              <div className="space-y-10 mt-10">
                {openSourceProjects.map((project) => (
                  <div key={project.name} className="space-y-4">
                    <h2 className={`text-lg sm:text-xl font-semibold ${theme.text} flex items-center gap-2`}>
                      <span>📦</span>
                      <span>
                        {project.repoUrl ? (
                          <a
                            href={project.repoUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={`${theme.accent} hover:opacity-80 underline transition-colors`}
                          >
                            {project.name}
                          </a>
                        ) : (
                          project.name
                        )}
                      </span>
                    </h2>
                    <ul className="space-y-3 list-none pl-0">
                      {project.prs.map((pr, index) => (
                        <li key={index} className="space-y-1">
                          <div className="flex flex-wrap items-baseline gap-2 gap-y-1">
                            <a
                              href={pr.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={`font-medium ${theme.accent} hover:opacity-80 underline transition-colors`}
                            >
                              {pr.title}
                            </a>
                            {pr.date && (
                              <span
                                className={`inline-flex items-center text-xs font-medium tabular-nums ${theme.text}/50 bg-black/10 dark:bg-white/10 px-2 py-0.5 rounded`}
                                aria-label={`Opened ${formatPRDate(pr.date)}`}
                              >
                                {formatPRDate(pr.date)}
                              </span>
                            )}
                          </div>
                          {pr.description && (
                            <p className={`text-sm ${theme.text}/60 mt-0.5`}>
                              {pr.description}
                            </p>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>

              {/* Footer note */}
              <p className={`text-sm mt-12 ${theme.text}/50 italic`}>
                This page is updated as I contribute to more projects.
              </p>
            </div>
          </section>

          {/* Social Links and Footer - Matching home page style */}
          <section className={`mt-16 sm:mt-20 md:mt-24 flex flex-col items-center gap-6 ${theme.text}/60`}>
            <div className="flex items-center gap-6">
              <a
                href={siteConfig.social.github}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="GitHub"
                className="transition-colors hover:opacity-100"
              >
                <Github className="h-6 w-6 sm:h-7 sm:w-7" />
              </a>
              <a
                href={siteConfig.social.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="LinkedIn"
                className="transition-colors hover:opacity-100"
              >
                <Linkedin className="h-6 w-6 sm:h-7 sm:w-7" />
              </a>
              <a
                href={`mailto:${siteConfig.email}`}
                aria-label="Email"
                className="transition-colors hover:opacity-100"
              >
                <Mail className="h-6 w-6 sm:h-7 sm:w-7" />
              </a>
            </div>
            <p className="text-xs sm:text-sm flex flex-wrap items-center justify-center gap-2">
              <span>&gt; built with</span>
              <span className="inline-flex h-4 overflow-hidden align-middle">
                <span className="rotate-words">
                  <span>Cursor</span>
                  <span>love</span>
                  <span>coffee</span>
                  <span>code</span>
                </span>
              </span>
            </p>
          </section>
        </main>
      </div>
    </div>
  );
};

export default OpenSource;
